import List from '@pelckmans/elementis/components/list';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { isScorableLti, UserMaterial } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../contexts/ModalContext';
import ModuleContext from '../../../_contexts/ModuleContext';
import { MaterialIcon } from '../../../components/MaterialIcon';
import SelectionContext from '../../_contexts/SelectionContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import useUserMaterial from '../../hooks/user-material/useUserMaterial';
import MaterialPills from '../MaterialPills';
import PinnedMaterialButton from '../pinned-material/PinnedMaterialButton';
import ScorableProgress from '../ScorableProgress';
import MaterialListHeader from '../MaterialListHeader';

type UserMaterialListProps = {
  userMaterial: UserMaterial[];
  title: string;
};
export default function UserMaterialList({ userMaterial, title }: UserMaterialListProps) {
  const { t } = useTranslation();

  const { showModal } = useContext(ModalContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { selectedModule } = useContext(ModuleContext);
  const { showSelection, selectedUserMaterials, addUserMaterials, removeUserMaterials } = useContext(SelectionContext);

  const { openMaterial, fetchUserMaterial } = useUserMaterial();

  if (!selectedModule || !selectedNode) return null;

  const handleOpenMaterial = async (material: UserMaterial) => {
    const modal = await openMaterial(
      material,
      material.userMaxScore ? fetchUserMaterial : undefined,
      ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
    );

    if (modal) {
      showModal(modal);
    }
  };

  const renderListItem = (material: UserMaterial) => {
    // TODO no action context menu when showSelection is active
    const actionArea = (
      <>
        {isScorableLti(material) && <ScorableProgress score={material.score} />}
        <PinnedMaterialButton type="user-material" materialId={material.id} />
      </>
    );

    const pillGroupProps = {
      'aria-label': t('PAGES.TOC_LEVEL.MATERIAL_PILLS.A11Y_LABEL'),
      children: <MaterialPills isShared={material.shared} isAssignment={!!material.assignment} />,
      role: 'group',
    };

    if (showSelection) {
      const isSelected = selectedUserMaterials.includes(material.id);
      return (
        <List.Item
          key={material.id}
          title={material.name}
          actionArea={actionArea}
          pillGroupProps={pillGroupProps}
          onClick={() => (isSelected ? removeUserMaterials([material.id]) : addUserMaterials([material.id]))}
          as="checkbox"
          checked={selectedUserMaterials.includes(material.id)}
        />
      );
    }
    return (
      <List.Item
        key={material.id}
        title={material.name}
        icon={<MaterialIcon materialType="user-material" />}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        onClick={() => handleOpenMaterial(material)}
        as="button"
      />
    );
  };

  return (
    <List>
      <MaterialListHeader
        title={title}
        showSelection={showSelection}
        selectedMaterial={selectedUserMaterials}
        material={userMaterial}
        addMaterial={addUserMaterials}
        removeMaterial={removeUserMaterials}
      />
      {userMaterial.map(u => renderListItem(u))}
    </List>
  );
}
